import {
    type OrigemRegistro,
    Origens,
    OrigensEnum,
    corDaMarca,
    parserRegisterOrigin,
} from '@acme/common'
import { useThemeStore } from 'global/components/ToggleTheme'
import type { LucideProps } from 'lucide-react'
import {
    ArrowBigLeftIcon,
    ArrowBigRightIcon,
    BetweenVerticalEndIcon,
    CalendarCheckIcon,
    ClockCheckIcon,
    FileClockIcon,
    FingerprintIcon,
    GlobeIcon,
    KeyboardIcon,
    MonitorCogIcon,
    PencilSparklesIcon,
    ScanFaceIcon,
    SmartphoneIcon,
    TabletSmartphoneIcon,
    UserPenIcon,
} from 'lucide-react'
import type { ComponentType } from 'react'

type Direcao = 'anterior' | 'seguinte'

interface Props {
    origem: number
    /** Ícone sobreposto quando o registro pertence a outro dia */
    direcao?: Direcao
    title?: string
}

/** Assinatura de ícone da lucide — qualquer `XIcon` de `lucide-react` encaixa. */
type Icone = ComponentType<LucideProps>

/**
 * Ponto de troca dos ícones: uma entrada por origem. Trocar o ícone de uma
 * origem é trocar só a linha dela — nada mais no sistema depende de qual ícone
 * cada origem usa. A cor vem da natureza (PALETA_NATUREZAS, em @acme/common):
 * quatro cores no total, uma por natureza — o ícone é quem identifica a origem.
 */
const ICONES: Record<OrigemRegistro, Icone> = {
    [OrigensEnum.MANUAL_SUPERVISOR]: PencilSparklesIcon,
    [OrigensEnum.MANUAL_FUNCIONARIO]: UserPenIcon,
    [OrigensEnum.MANUAL_TELA_DIGITACAO_GESTORP]: MonitorCogIcon,
    [OrigensEnum.BIOMETRIA_REP_TERMINAL]: FingerprintIcon,
    [OrigensEnum.TECLADO_REP_TERMINAL]: KeyboardIcon,
    [OrigensEnum.REP]: ClockCheckIcon,
    [OrigensEnum.APLICATIVO_PONTO]: SmartphoneIcon,
    [OrigensEnum.APLICATIVO_PONTO_MANUAL]: SmartphoneIcon,
    [OrigensEnum.APLICATIVO_PONTO_WEB]: GlobeIcon,
    [OrigensEnum.APLICATIVO_PONTO_COLETIVO]: TabletSmartphoneIcon,
    [OrigensEnum.PONTO_EXCECAO]: BetweenVerticalEndIcon,
    [OrigensEnum.PONTO_PRE_ASSINALADO]: CalendarCheckIcon,
    [OrigensEnum.CONTROLE_ACESSO]: ScanFaceIcon,
    [OrigensEnum.IMPORTACAO_AFD]: FileClockIcon,
}

/** Ícone da seta de dia vizinho, sobreposto ao ícone da origem. */
const ICONES_DIRECAO: Record<Direcao, Icone> = {
    anterior: ArrowBigLeftIcon,
    seguinte: ArrowBigRightIcon,
}

export function MarcaOrigem({ origem, direcao, title }: Props) {
    const mode = useThemeStore(state => state.theme)
    const IconDirEnum = direcao ? ICONES_DIRECAO[direcao] : null,
        IconeDirecao = () =>
            IconDirEnum ? (
                <span className="-right-1 -bottom-1 absolute inline-flex rounded-full bg-card">
                    <IconDirEnum className="size-3.5" aria-hidden />
                </span>
            ) : null

    if (!(origem in Origens))
        return (
            <span
                className="relative inline-flex items-center"
                role="img"
                aria-label={'Origem não identificada'}
                title={'Origem não identificada'}
            >
                <div
                    className="rounded-full size-4.5 bg-gray-500"
                    aria-hidden
                />
                <IconeDirecao />
            </span>
        )

    const id = origem as OrigemRegistro
    const { color, title: nome } = parserRegisterOrigin(id)
    const cor = corDaMarca(color, mode)

    const Icone = ICONES[id]

    return (
        <span
            className="relative inline-flex items-center"
            style={{ color: cor }}
            role="img"
            aria-label={title ?? nome}
            title={title ?? nome}
        >
            <Icone className="size-4.5 stroke-[2.5]" aria-hidden />
            <IconeDirecao />
        </span>
    )
}
